import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getServers } from '../../services/refurbishedServerService';
import './RefurbishedServers.css';

const formatPrice = (n) => `$${Number(n).toLocaleString('en-US')}`;

const discountPercent = (original, sale) => {
  if (!original || original <= sale) return null;
  return Math.round(((original - sale) / original) * 100);
};

// Category tree is intentionally data-driven so more product types can be
// added later without changing the sidebar layout.
const CATEGORY_TREE = [
  {
    id: 'refurbished-products',
    label: 'Refurbished Products',
    subCategories: [
      { id: 'servers', label: 'Servers' },
      // Add future categories here:
      // { id: 'monitors', label: 'Monitors' },
      // { id: 'cables', label: 'Cables' },
    ],
  },
];

// Availability copy is centralized here so the status dot, label, and
// sub-line always stay in sync for a given server.
const getAvailability = (server) => {
  const soldOut = server.status === 'SOLD' || server.quantityAvailable <= 0;
  const reserved = server.status === 'RESERVED';

  if (soldOut) {
    return {
      tone: 'soldout',
      label: 'Sold Out',
      detail: 'Currently unavailable.',
    };
  }

  if (reserved) {
    return {
      tone: 'reserved',
      label: 'Reserved',
      detail: 'Contact us to check availability.',
    };
  }

  return {
    tone: 'available',
    label: 'In Stock',
    detail: `${server.quantityAvailable} available`,
  };
};

export default function RefurbishedServers() {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const [expandedCategory, setExpandedCategory] = useState(
    'refurbished-products'
  );
  const [activeSubCategory, setActiveSubCategory] = useState('servers');

  // Per-row UI state, keyed by server id.
  const [expandedSpecs, setExpandedSpecs] = useState({});
  const [compared, setCompared] = useState({});
  const [quantities, setQuantities] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await getServers();
        setServers(data);
      } catch (e) {
        setError(
          e?.response?.data?.message || 'Failed to load server listings'
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const getQuantity = (server) => quantities[server.id] ?? 1;

  const stepQuantity = (server, delta) => {
    const max = Math.max(server.quantityAvailable || 1, 1);
    setQuantities((prev) => {
      const current = prev[server.id] ?? 1;
      const next = Math.min(Math.max(current + delta, 1), max);
      return { ...prev, [server.id]: next };
    });
  };

  const toggleSpecs = (serverId) => {
    setExpandedSpecs((prev) => ({ ...prev, [serverId]: !prev[serverId] }));
  };

  const toggleCompare = (serverId) => {
    setCompared((prev) => ({ ...prev, [serverId]: !prev[serverId] }));
  };

  const contactForPurchase = (server) => {
    const quantity = getQuantity(server);
    navigate('/contact', {
      state: {
        prefillComment: `I'm interested in purchasing ${quantity} x ${server.name} (${formatPrice(
          server.salePrice
        )} each). Please contact me with more details.`,
        prefillAreaOfInterest: 'Refurbished Servers',
      },
    });
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategory((prev) => (prev === categoryId ? null : categoryId));
  };

  const visibleProducts =
    activeSubCategory === 'servers' ? servers : [];

  const activeSubCategoryLabel =
    CATEGORY_TREE.flatMap((category) => category.subCategories).find(
      (subcategory) => subcategory.id === activeSubCategory
    )?.label || '';

  return (
    <div className="rs-page">
      <div className="rs-container">
        <header className="rs-header">
          <span className="rs-eyebrow">
            <span className="rs-live-dot" aria-hidden="true" />
            Hardware Marketplace
          </span>

          <h1>Products</h1>

          
        </header>

        {error ? <div className="rs-alert">{error}</div> : null}
        {loading ? <div className="rs-alert">Loading listings...</div> : null}

        {!loading && !error ? (
          <div className="rs-layout">
            <aside className="rs-sidebar">
              <div className="rs-sidebar-label">Categories</div>

              <nav
                className="rs-category-tree"
                aria-label="Product categories"
              >
                {CATEGORY_TREE.map((category) => {
                  const expanded = expandedCategory === category.id;

                  return (
                    <div
                      key={category.id}
                      className="rs-category-group"
                    >
                      <button
                        type="button"
                        className="rs-category-parent"
                        onClick={() => toggleCategory(category.id)}
                        aria-expanded={expanded}
                      >
                        <span
                          className="rs-category-icon"
                          aria-hidden="true"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M9 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6h-3" />
                            <path d="M13 2v6h6" />
                          </svg>
                        </span>

                        <span className="rs-category-name">
                          {category.label}
                        </span>

                        <span
                          className="rs-category-toggle"
                          aria-hidden="true"
                        >
                          {expanded ? '−' : '+'}
                        </span>
                      </button>

                      {expanded ? (
                        <div className="rs-subcategory-list">
                          {category.subCategories.map((sub) => (
                            <button
                              key={sub.id}
                              type="button"
                              className={`rs-subcategory-item ${
                                activeSubCategory === sub.id ? 'active' : ''
                              }`}
                              onClick={() =>
                                setActiveSubCategory(sub.id)
                              }
                            >
                              <span
                                className="rs-subcategory-dot"
                                aria-hidden="true"
                              />
                              <span>{sub.label}</span>
                              <span
                                className="rs-subcategory-arrow"
                                aria-hidden="true"
                              >
                                →
                              </span>
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </nav>
            </aside>

            <main className="rs-content">
              <div className="rs-content-header">
                <div>
                  <span className="rs-content-eyebrow">
                    Refurbished Products
                  </span>
                  <h2 className="rs-content-title">
                    {activeSubCategoryLabel}
                  </h2>
                </div>

                <span className="rs-count-badge">
                  {visibleProducts.length} PRODUCT
                  {visibleProducts.length === 1 ? '' : 'S'}
                </span>
              </div>

              <div className="rs-list">
                {visibleProducts.map((server) => {
                  const pct = discountPercent(
                    server.originalPrice,
                    server.salePrice
                  );
                  const availability = getAvailability(server);
                  const soldOut = availability.tone === 'soldout';
                  const specsOpen = !!expandedSpecs[server.id];
                  const isCompared = !!compared[server.id];
                  const quantity = getQuantity(server);

                  return (
                    <article
                      key={server.id}
                      className={`rs-row ${
                        soldOut ? 'rs-row--soldout' : ''
                      }`}
                    >
                      <div className="rs-row__media">
                        {server.images?.[0] ? (
                          <div className="rs-row__image">
                            <img
                              src={server.images[0]}
                              alt={server.name}
                              loading="lazy"
                            />
                          </div>
                        ) : (
                          <div className="rs-row__image rs-row__image--placeholder">
                            <span>{server.brand || 'Server'}</span>
                          </div>
                        )}

                        <label className="rs-compare">
                          <input
                            type="checkbox"
                            checked={isCompared}
                            onChange={() => toggleCompare(server.id)}
                          />
                          <span>Compare</span>
                        </label>
                      </div>

                      <div className="rs-row__details">
                        <h3 className="rs-row__title">{server.name}</h3>

                        <div className="rs-row__ids">
                          {server.brand ? (
                            <span>MFG#: {server.brand}</span>
                          ) : null}
                          {server.brand ? (
                            <span
                              className="rs-row__id-sep"
                              aria-hidden="true"
                            >
                          
                            </span>
                          ) : null}
                        </div>

                        <dl className="rs-row__specs">
                          {server.cpu ? (
                            <div className="rs-row__spec">
                              <dt>Processor Type:</dt>
                              <dd>{server.cpu}</dd>
                            </div>
                          ) : null}

                          {server.ram ? (
                            <div className="rs-row__spec">
                              <dt>RAM Installed:</dt>
                              <dd>{server.ram}</dd>
                            </div>
                          ) : null}

                          {server.storage ? (
                            <div className="rs-row__spec">
                              <dt>Storage:</dt>
                              <dd>{server.storage}</dd>
                            </div>
                          ) : null}

                          {server.formFactor ? (
                            <div className="rs-row__spec">
                              <dt>Form Factor:</dt>
                              <dd>{server.formFactor}</dd>
                            </div>
                          ) : null}
                        </dl>

                        {server.description ? (
                          <>
                            <button
                              type="button"
                              className="rs-row__expand"
                              onClick={() => toggleSpecs(server.id)}
                              aria-expanded={specsOpen}
                            >
                              {specsOpen ? '[−] Hide Specs' : '[+] Expand Specs'}
                            </button>

                            {specsOpen ? (
                              <p className="rs-description">
                                {server.description}
                              </p>
                            ) : null}
                          </>
                        ) : null}
                      </div>

                      <div className="rs-row__purchase">
                        <div className="rs-row__status">
                          <span
                            className={`rs-status-dot rs-status-dot--${availability.tone}`}
                            aria-hidden="true"
                          />
                          <div>
                            <span className="rs-row__status-label">
                              {availability.label}
                            </span>
                            <span className="rs-row__status-detail">
                              {availability.detail}
                            </span>
                          </div>
                        </div>

                        <div className="rs-row__price">
                          {pct ? (
                            <span className="rs-price-original">
                              {formatPrice(server.originalPrice)}
                            </span>
                          ) : null}
                          <span className="rs-price-sale">
                            {formatPrice(server.salePrice)}
                          </span>
                          <span className="rs-price-label">
                            Discount Price
                            {pct ? ` · ${pct}% off` : ''}
                          </span>
                        </div>

                        {!soldOut ? (
                          <div className="rs-qty-row">
                            <div className="rs-qty-stepper">
                              <input
                                className="rs-qty-input"
                                type="text"
                                inputMode="numeric"
                                readOnly
                                value={quantity}
                                aria-label={`Quantity for ${server.name}`}
                              />
                              <div className="rs-qty-buttons">
                                <button
                                  type="button"
                                  aria-label="Increase quantity"
                                  onClick={() => stepQuantity(server, 1)}
                                  disabled={
                                    quantity >= (server.quantityAvailable || 1)
                                  }
                                >
                                  ▲
                                </button>
                                <button
                                  type="button"
                                  aria-label="Decrease quantity"
                                  onClick={() => stepQuantity(server, -1)}
                                  disabled={quantity <= 1}
                                >
                                  ▼
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : null}

                        <button
                          type="button"
                          className="rs-contact-btn"
                          disabled={soldOut}
                          onClick={() => contactForPurchase(server)}
                        >
                          {soldOut ? 'Sold Out' : 'Contact for Purchase'}
                        </button>
                      </div>
                    </article>
                  );
                })}

                {visibleProducts.length === 0 ? (
                  <div className="rs-alert rs-alert--empty">
                    No listings available right now — check back soon.
                  </div>
                ) : null}
              </div>
            </main>
          </div>
        ) : null}
      </div>
    </div>
  );
}